import React, { Component } from 'react';
import classNames from 'classnames';
import { __, mapStateToProps, setTableParams } from '../core/utils';
import { setNotification } from '../redux/actions/general/notification';
import { hideAlertbox, showAlertbox } from '../redux/actions/general/alertbox';
import {
  deletePlanningTemplate,
  duplicatePlanningTemplate,
  listPlanningTemplates,
  readPlanningTemplate,
} from '../redux/actions/table/planning_library';
import Button from '../components/Button';
import Tooltip from '../components/Tooltip';
import SearchIcon from '../assets/images/search-15x15.svg';
import AddTemplateIcon from '../assets/images/add-boq-template-group-60x60.svg';
import LogoIcon from '../assets/images/logo-50x50.svg';
import { planningTemplateFromResponse } from '../views/company/PlanningTemplates/helpers';
import styles from '../styles/wizards/PlanningTemplateWizard.scss';

@mapStateToProps((state) => ({
  templates: state.table.planning_library.templates || [],
  canAddTemplate: Boolean(
    _.get(
      state.table,
      'planning_library.template_chooser.canAddTemplate',
      false,
    ),
  ),
  canDeleteSystem: Boolean(
    _.get(
      state.table,
      'planning_library.template_chooser.canDeleteSystem',
      false,
    ),
  ),
  isSystem: Boolean(
    _.get(state.table, 'planning_library.template_chooser.isSystem', false),
  ),
}))
class PlanningTemplateChooserWizard extends Component {
  state = {
    search: '',
    selectedId: null,
    preview: null,
  };

  componentDidMount() {
    listPlanningTemplates(this.props.isSystem).then(({ response }) => {
      const first = _.first(response.data);

      if (first) {
        this.selectTemplate(first.id);
      }
    });
  }

  selectTemplate(id) {
    this.setState({ selectedId: id });
    readPlanningTemplate(id, this.props.isSystem).then(({ response }) => {
      if (this.state.selectedId === id) {
        this.setState({ preview: response.data });
      }
    });
  }

  openTemplate() {
    if (!this.state.preview) {
      return;
    }

    setTableParams('planning_library', {
      template_draft: planningTemplateFromResponse(this.state.preview),
      template_dirty: false,
    });
    this.props.onClose();
  }

  duplicateTemplate() {
    if (
      !this.state.selectedId
      || (this.props.isSystem && !this.props.canAddTemplate)
    ) {
      return;
    }

    duplicatePlanningTemplate(
      this.state.selectedId,
      {},
      this.props.isSystem,
    ).then(({ response }) => {
      listPlanningTemplates(this.props.isSystem)
        .then(() => this.selectTemplate(response.data.id));
      setNotification(
        __('planning-templates.template.duplicated')
      );
    });
  }

  deleteTemplate() {
    if (
      !this.state.preview
      || (this.state.preview.is_system && !this.props.canDeleteSystem)
    ) {
      return;
    }

    const template = this.state.preview;

    showAlertbox({
      color: 'red',
      title: __('general.alert.are-you-sure'),
      description: __('planning-templates.template.delete-description'),
      buttons: [
        {
          color: 'lightGray',
          text: __('general.alert.no-close'),
          onClick: 'close',
        },
        {
          color: 'gray',
          text: __('general.alert.yes-delete'),
          onClick: () => {
            deletePlanningTemplate(template.id, this.props.isSystem).then(() => {
              hideAlertbox();
              this.setState({ selectedId: null, preview: null });
              listPlanningTemplates(this.props.isSystem);
              setNotification(
                __('planning-templates.template.deleted')
              );
            });
          },
        },
      ],
    });
  }

  renderNodes(nodes) {
    return _.map(nodes, (node) => (
      <React.Fragment key={node.id}>
        <div
          className={styles.previewNode}
          style={{
            paddingLeft: `${
              20 +
              (node.type === 'substage' ? 28 : node.type === 'group' ? 56 : 0)
            }px`,
          }}
        >
          <strong>{node.name}</strong>
          <span>{node.type}</span>
        </div>
        {this.renderNodes(node.children)}
      </React.Fragment>
    ));
  }

  render() {
    const search = _.toLower(_.trim(this.state.search));
    const templates = _.filter(this.props.templates, (template) =>
      _.includes(_.toLower(template.name), search)
    );
    const preview = this.state.preview;

    return (
      <div className={styles.templateChooser}>
        <header className={styles.chooserHeader}>
          <div className={styles.chooserTitle}>
            <strong>
              {__('planning-templates.chooser.title')}
            </strong>
            <span>
              {this.props.isSystem
                ? __('planning-templates.chooser.system-subtitle')
                : __('planning-templates.chooser.subtitle')}
            </span>
          </div>
          <AddTemplateIcon className={styles.chooserIcon} />
        </header>

        <div className={styles.chooserBodyWrapper}>
          <div className={styles.chooserBody}>
            <div className={styles.templateListPanel}>
              <div className={styles.chooserSearch}>
                <SearchIcon />
                <input
                  autoFocus
                  type='text'
                  value={this.state.search}
                  placeholder={__('planning-templates.chooser.search')}
                  onChange={(event) =>
                    this.setState({ search: event.target.value })
                  }
                />
              </div>
              <div className={styles.templateList}>
                {_.map(templates, (template) => (
                  <button
                    type='button'
                    key={template.id}
                    className={classNames(
                      styles.templateListItem,
                      template.id === this.state.selectedId && styles.selected
                    )}
                    onClick={() => this.selectTemplate(template.id)}
                  >
                    <strong>{template.name}</strong>
                    <span>
                      {template.stages_count} stages · {template.substages_count}{' '}
                      substages · {template.groups_count} groups
                    </span>
                    <Tooltip
                      text={
                        template.is_system
                          ? __('planning-templates.source.system')
                          : __('planning-templates.source.my-company')
                      }
                    >
                      <em
                        className={classNames(
                          styles.sourceLabel,
                          template.is_system ? styles.system : styles.company
                        )}
                      >
                        {template.is_system ? <LogoIcon /> : 'MY'}
                      </em>
                    </Tooltip>
                  </button>
                ))}
              </div>
            </div>

            <div className={styles.templatePreview}>
              {preview && (
                <>
                  <h2>{preview.name}</h2>
                  <p>
                    {preview.stages_count} stages · {preview.substages_count}{' '}
                    substages · {preview.groups_count} groups
                  </p>
                  <div className={styles.previewTree}>
                    {_.isEmpty(preview.nodes) ? (
                      <div className={styles.empty}>Empty template</div>
                    ) : (
                      this.renderNodes(preview.nodes)
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <footer className={styles.chooserFooter}>
          <div>
            <Button
              lightGray
              medium
              middleText={__('button.duplicate')}
              onClick={() => this.duplicateTemplate()}
              disabled={
                !preview
                || (this.props.isSystem && !this.props.canAddTemplate)
              }
            />
            {preview
              && (!preview.is_system || this.props.canDeleteSystem) && (
              <Button
                lightGray
                medium
                middleText={__('button.delete')}
                onClick={() => this.deleteTemplate()}
              />
            )}
          </div>
          <div>
            <Button
              lightGray
              medium
              middleText={__('button.cancel')}
              onClick={this.props.onClose}
            />
            <Button
              medium
              middleText={__('button.open')}
              onClick={() => this.openTemplate()}
              disabled={!preview}
            />
          </div>
        </footer>
      </div>
    );
  }
}

export default PlanningTemplateChooserWizard;
